import axios from "axios";
import VueCookie from "vue-cookie";
import router from "../routes/router";
import swal from "sweetalert2";
import i18n from "../store/i18n";
import { deleteAuthCookie } from "../util/auth-cookie";
import { platformErrorCode, platformErrorText } from "../util/platform-error";
const qs = require("querystring");

const HttpMethod = {
    Get: "GET",
    Post: "POST",
    Put: "PUT",
    Patch: "PATCH",
    Delete: "DELETE"
};

let axiosInstance = axios.create({
    baseURL: process.env.VUE_APP_API_BASE_URL,
    timeout: 300000,
    maxContentLength: Infinity,
    maxBodyLength: Infinity
});

axiosInstance.interceptors.request.use(
    function(config) {
        // if ( config.method === "POST" || config.method === "PATCH" || config.method === "PUT") {
        //     config.headers["Content-Type"] = "application/json;charset=utf-8";
        // }

        if (VueCookie.get("token") != "") {
            config.headers["Authorization"] = "Bearer " + VueCookie.get("token");
        }

        return config;
    },
    function(error) {
        // Do something with request error
        return Promise.reject(error);
    }
);

// Add a response interceptor
axiosInstance.interceptors.response.use(
    function(response) {
        // Do something with response data
        return response;
    },
    function(error) {
        // Do something with response error
        const errUrl = (error.config && error.config.url) || "";
        // LINE auth endpoints (spec 2026-10-03 §2) answer 403 `line_not_linked`
        // and 401 `auth.block` / `auth.unverify` as part of their normal
        // contract, and the caller turns those into a sentence that tells the
        // staff member what to do next. Both blanket handlers below would eat
        // that: the 403 one fires "คุณไม่มีสิทธิ์เข้าใช้งานระบบนี้" on top of
        // our swal, and the 401 one redirects to /login with no message at all.
        const isLineAuthEndpoint = errUrl.indexOf("/line/callback") !== -1 ||
                                   errUrl.indexOf("/line/authorize-url") !== -1 ||
                                   errUrl.indexOf("/profile/line") !== -1;
        // 403 = authenticated but not authorized (e.g. a read-only user hitting
        // a write handler). Do NOT log the user out — just surface a permission
        // error and reject so the caller can react. Only 401 (expired/invalid
        // token) triggers the logout+redirect flow below.
        if (error.response && error.response.status === 403) {
            if (isLineAuthEndpoint) {
                return Promise.reject(error);
            }
            swal.fire({ title: i18n.t("errors.permission_denied"), icon: "error" });
            return Promise.reject(error);
        }
        // 424 / 503 platform_* = the SHOP's marketplace token (TikTok, LINE
        // SHOPPING, Pancake) is unusable or the integration is off — NOT the
        // staff session. Toast and reject; never the logout below
        // (util/platform-error.js).
        if (platformErrorCode(error)) {
            swal.fire({
                toast: true,
                position: "top-end",
                icon: "warning",
                title: platformErrorText(error, i18n.locale),
                showConfirmButton: false,
                timer: 8000
            });
            return Promise.reject(error);
        }
        // 401 = the staff token itself is missing / invalid / expired. Role
        // gates answer 403 (above) since bo-api #937, platform gates 424.
        if (error.response && error.response.status === 401) {
            // Login endpoint must surface its 401 to the caller so the form
            // can render an "incorrect credentials" swal. The auto-redirect
            // below is meant for expired-token cases on protected endpoints.
            const url = errUrl;
            const isAuthEndpoint = url.indexOf("/auth/login") !== -1 ||
                                   url.indexOf("/auth/forgotpassword") !== -1 ||
                                   url.indexOf("/auth/resetpassword") !== -1 ||
                                   // /api/auth/line/callback: a blocked or
                                   // unverified staff account is a 401 here and
                                   // the page has to print the same wording the
                                   // password form prints (spec §6 ข้อ 2).
                                   url.indexOf("/auth/line/callback") !== -1;
            if (isAuthEndpoint) {
                return Promise.reject(error);
            }
            // Close any open loading modals
            swal.close();
            // Clear token (parent-domain scope) and redirect to login.
            deleteAuthCookie(VueCookie);
            router.push("/login");
            // Return a promise that never resolves to prevent error popups
            return new Promise(function() {});
        }
        return Promise.reject(error);
    }
);

class HttpRequest {

    request(method,url, payload) {
        let promise = null;

		// Check Payload is formdata
		if (payload instanceof FormData) {
			if (method === HttpMethod.Post) {
				axiosInstance.defaults.headers.post['Content-Type'] = 'multipart/form-data';
			} else if (method === HttpMethod.Put) {
				axiosInstance.defaults.headers.put['Content-Type'] = 'multipart/form-data';
			} else if (method === HttpMethod.Patch) {
				axiosInstance.defaults.headers.patch['Content-Type'] = 'multipart/form-data';
			}
		} else {
			if (method === HttpMethod.Post) {
				axiosInstance.defaults.headers.post['Content-Type'] = 'application/json';
			} else if (method === HttpMethod.Put) {
				axiosInstance.defaults.headers.put['Content-Type'] = 'application/json';
			} else if (method === HttpMethod.Patch) {
				axiosInstance.defaults.headers.patch['Content-Type'] = 'application/json';
			}

		}

        switch (method) {
            case HttpMethod.Get:
                promise = axiosInstance.get(url, {params : payload});
                break;
            case HttpMethod.Post:
                promise = axiosInstance.post(url, payload);
                break;
            case HttpMethod.Put:
                promise = axiosInstance.put(url, payload);
                break;
            case HttpMethod.Patch:
                promise = axiosInstance.patch(url, payload);
                break;
            case HttpMethod.Delete:
                promise = axiosInstance.delete(url, payload);
                break;
            default:
                promise = axiosInstance.get(url, payload);
                break;
        }
        return promise;
    }
}

export { HttpRequest, HttpMethod, axiosInstance };
